import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDeeds } from "../services/api";
import { categorizeDeeds } from "../components/Deed/DeedList/deedListUtils";
import { useAuth } from "../components/Auth/AuthContext";

const sortDeeds = (deeds) =>
  [...deeds].sort((a, b) => {
    if (a.dateTime < b.dateTime) return 1;
    if (a.dateTime > b.dateTime) return -1;
    return 0;
  });

const generateAutocompleteOptions = (deeds) => {
  const buckets = [];
  const descriptions = [];
  deeds.forEach((deed) => {
    if (!buckets.includes(deed.bucket)) buckets.push(deed.bucket);
    if (!descriptions.includes(deed.description)) descriptions.push(deed.description);
  });
  const descriptionOptions = descriptions.map((description) => ({
    type: "Deed",
    title: description,
  }));
  const bucketOptions = buckets
    .map((bucket) =>
      bucket
        ? { type: "Bucket", title: bucket }
        : { type: "Bucket", title: "(none)", bucketValue: "" }
    )
    .sort((a) => (a.title === "(none)" ? 1 : -1));
  return [...descriptionOptions, ...bucketOptions];
};

const filterDeeds = (deeds, chipValues) => {
  const descriptionChip = chipValues.find((chip) => chip.type === "Deed");
  const bucketChip = chipValues.find((chip) => chip.type === "Bucket");

  return deeds.filter((deed) => {
    if (descriptionChip && deed.description !== descriptionChip.title) return false;
    if (bucketChip) {
      if (bucketChip.title === "(none)" && deed.bucket != null) return false;
      if (bucketChip.title !== "(none)" && deed.bucket !== bucketChip.title) return false;
    }
    return true;
  });
};

const groupByDay = (categorized) =>
  Object.entries(categorized).map(([date, deeds]) => ({
    label: date,
    deeds,
  }));

export const DEEDS_QUERY_KEY = ["deeds"];

/** Single source of truth for fetching, sorting, filtering and grouping deeds. */
export default function useDeeds(chipValues = []) {
  const { currentUser } = useAuth();

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: DEEDS_QUERY_KEY,
    queryFn: async () => {
      const response = await getDeeds();
      return response.data;
    },
    enabled: !!currentUser,
    staleTime: 60_000,
  });

  const fetchedDeeds = useMemo(() => data ?? [], [data]);

  const sortedDeeds = useMemo(() => sortDeeds(fetchedDeeds), [fetchedDeeds]);

  const options = useMemo(
    () => generateAutocompleteOptions(fetchedDeeds),
    [fetchedDeeds]
  );

  const groupedDeeds = useMemo(() => {
    const filtered = filterDeeds(sortedDeeds, chipValues);
    return groupByDay(categorizeDeeds(filtered));
  }, [sortedDeeds, chipValues]);

  const totalCount = useMemo(
    () => groupedDeeds.reduce((sum, group) => sum + group.deeds.length, 0),
    [groupedDeeds]
  );

  return {
    sortedDeeds,
    groupedDeeds,
    options,
    totalCount,
    isLoading,
    isProcessing: isFetching && !isLoading,
    error,
  };
}
